import axios from '../api/axios';
import { config } from '../config';
import { CONSTANTS } from '../constants/contstants';
import { GenericOperationStatuses } from '../models/GenericOperationStatuses';
import {
  AssessmentIntegrityEventFilter,
  AssessmentIntegrityEventPage,
  AssessmentIntegrityEventRequest,
} from '../models/assessment-integrity-event';
import { Response } from '../models/response';
import { ResponseWithData } from '../models/responseWithData';

const eventQueues = new Map<string, Promise<void>>();

const getEventUrl = (userId: string, moduleProgressId: string): string => {
  const apiBaseUrl = new URL(config.apiBaseUrl, window.location.origin);
  return new URL(
    `sessions/${encodeURIComponent(userId)}/progress/${moduleProgressId}/integrity-events`,
    apiBaseUrl
  ).toString();
};

const getEventQuery = (
  pageNumber: number,
  pageSize: number,
  filter: AssessmentIntegrityEventFilter
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('pageNumber', String(pageNumber));
  params.set('pageSize', String(pageSize));
  if (filter.assignmentId) params.set('filter.assignmentId', filter.assignmentId);
  if (filter.examTakerId) params.set('filter.examTakerId', filter.examTakerId);
  if (filter.eventType) params.set('filter.eventType', filter.eventType);
  if (filter.suspiciousOnly) params.set('filter.suspiciousOnly', 'true');
  if (filter.fromUtc) params.set('filter.fromUtc', filter.fromUtc);
  if (filter.toUtc) params.set('filter.toUtc', filter.toUtc);
  return params;
};

export const assessmentIntegrityService = {
  recordEvent: (
    userId: string,
    moduleProgressId: string,
    request: AssessmentIntegrityEventRequest
  ): Promise<void> => {
    const key = `${userId}|${moduleProgressId}`;
    const previous = eventQueues.get(key) || Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(() => axios.post<Response<GenericOperationStatuses>>(getEventUrl(userId, moduleProgressId), request))
      .then(() => undefined)
      .catch(() => undefined);

    eventQueues.set(key, next);
    void next.then(() => {
      if (eventQueues.get(key) === next) eventQueues.delete(key);
    });

    return next;
  },

  recordEventKeepalive: (
    userId: string,
    moduleProgressId: string,
    request: AssessmentIntegrityEventRequest
  ): void => {
    const body = JSON.stringify(request);
    const url = getEventUrl(userId, moduleProgressId);
    const payload = new Blob([body], { type: 'application/json' });

    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(url, payload)) {
      return;
    }

    const token = localStorage.getItem(CONSTANTS.TOKEN_VARIABLE_NAME);
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body
    }).catch(() => undefined);
  },

  getEvents: async (
    pageNumber: number,
    pageSize: number,
    filter: AssessmentIntegrityEventFilter
  ): Promise<ResponseWithData<AssessmentIntegrityEventPage, GenericOperationStatuses>> => {
    const response = await axios.get<ResponseWithData<AssessmentIntegrityEventPage, GenericOperationStatuses>>(
      'assessment-integrity/events',
      { params: getEventQuery(pageNumber, pageSize, filter) }
    );
    return response.data;
  }
};
